import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { PayPalScriptProvider, PayPalButtons, FUNDING } from '@paypal/react-paypal-js';
import { AlertBanner, AlertBannerDescription } from '@hpstellar/core/visId';

import { dataLayerPush } from '../../../../metrics/metrics-helpers';
import withPageComponents from '../../../../page/components/with-page-components';
import useDeviceInfo from '../../../../hooks/useDeviceInfo';
import useCartState, { useCartActions } from '../../../../hooks/useCartState';
import { useIsPrivateStore } from '../../../../hooks/useUserData';
import { Helpers } from '../../../../core';
import usePageLoader from '../../../../hooks/usePageLoader';

const TermsAndConditions = () => {
    const [termsAndConditions, setTermsAndConditions] = useState();
    const { getTermsAndConditions } = useCartActions();
    useEffect(() => {
        if (!termsAndConditions) {
            getTermsAndConditions().then(setTermsAndConditions);
        }
    }, [termsAndConditions, getTermsAndConditions]);
    if (!termsAndConditions) {
        return null;
    }
    return (
        <div
            className="terms-and-conditions cust-html"
            dangerouslySetInnerHTML={Helpers.createMarkup(termsAndConditions)}
        />
    );
};
const getPayPalOptions = (paypalSDKURL, options) => {
    if (!paypalSDKURL) {
        return options;
    }
    //parse the url to get the query params
    const url = new URL(paypalSDKURL);
    const searchParams = url.searchParams;
    let sdkOptions = {};
    //loop through the query params and add them to the options change key to camelCase
    searchParams.forEach((value, key) => {
        sdkOptions[key.replace(/-([a-z])/g, g => g[1].toUpperCase())] = value;
    });
    //fallback to the default options if the clientId is not provided
    return sdkOptions.clientId ? sdkOptions : options;
};

const RETURN_URL_PATH = 'PayPalGetExpressCheckout?';

// Custom hook to get PayPal SDK options from cart state
const usePayPalSDKOptions = cartSettings => {
    const { payPal } = cartSettings || {};
    const { cartInfo, cartDetailsIsLoading } = useCartState();
    const { cartDetails, cartView } = cartInfo || {};
    const { cartUrls } = cartDetails || {};
    const { paypalSDKURL } = cartUrls || {};
    const { x_isPayPalEnabledForPstore, x_isPayPalEnabled } = cartView || {};
    const isPrivateStore = useIsPrivateStore();

    const paypalSDKOptions = useMemo(() => getPayPalOptions(paypalSDKURL, payPal), [paypalSDKURL, payPal?.clientId]);

    const isEnabled = !!(
        paypalSDKOptions?.clientId &&
        !(isPrivateStore && x_isPayPalEnabledForPstore === 'false') &&
        x_isPayPalEnabled !== 'false' &&
        !cartDetailsIsLoading
    );

    return { paypalSDKOptions, isEnabled, cartDetailsIsLoading };
};

export const PayPalProvider = withPageComponents(
    ({ children, cartSettings }) => {
        const { paypalSDKOptions, isEnabled } = usePayPalSDKOptions(cartSettings);

        // Only render provider if we have valid options
        if (!isEnabled) {
            return children;
        }

        return <PayPalScriptProvider options={paypalSDKOptions}>{children}</PayPalScriptProvider>;
    },
    { components: ['cartSettings'] },
);

export default withPageComponents(
    React.memo(
        ({ cartSettings, disabled, header, btnStyle = {}, disableLabelMessage, showTAndC = true, scrollToError }) => {
            const { payPal } = cartSettings || {};
            const { key, fundingSource = 'paypal' } = payPal || {};
            const { device } = useDeviceInfo(['device'], 'default');
            const isPrivateStore = useIsPrivateStore();
            const [isLoaded, setIsLoaded] = usePageLoader();
            const { payPalExpressCheckout, updatePaypalShippingAddress } = useCartActions();
            const { cartInfo } = useCartState();
            const { payPalReturnURL, id: orderId, cartView, cartDetails } = cartInfo || {};
            const {
                x_paypalLabelMessage,
                x_isPayPalEnabledForPstore,
                x_isPayPalEnabled,
                x_payPalShippingAddressCallbackToggle,
            } = cartView || {};
            const { cartUrls } = cartDetails || {};
            const { paypalExpressURL, paypalGetExpressURL } = cartUrls || {};

            // Use our custom hook instead of PayPal's reducer hook
            const { paypalSDKOptions } = usePayPalSDKOptions(cartSettings);

            const derivedPayPalReturnURL =
                paypalGetExpressURL && (payPalReturnURL || '').indexOf(RETURN_URL_PATH) > -1
                    ? payPalReturnURL.replace(RETURN_URL_PATH, paypalGetExpressURL)
                    : payPalReturnURL;
            const returnUrl = `${Helpers.decodeHtml(derivedPayPalReturnURL)}${orderId}`;
            const onButtonClick = useCallback(
                (data, actions) => {
                    // Prevent PayPal flow if validation fails
                    if (disabled && scrollToError) {
                        scrollToError();
                        return actions.reject();
                    }

                    try {
                        const { fundingSource } = data || {};
                        let paymentProvider = 'paypal';
                        if (fundingSource !== FUNDING.PAYPAL) {
                            paymentProvider = `paypal-${fundingSource}`;
                        }
                        dataLayerPush('standards.checkoutFrame', { paymentProvider });
                    } catch (e) {}

                    return actions.resolve();
                },
                [disabled, scrollToError],
            );

            const handlePaypalShippingAddressChange = useCallback(
                (data, actions) => {
                    const { city, state, postalCode, countryCode } = data.shippingAddress;
                    const token = data.orderID;

                    // Split postal code if it contains a dash
                    let zipCode = postalCode;
                    let addonZipcode = postalCode;

                    if (postalCode && postalCode.includes('-')) {
                        const [mainZip, addon] = postalCode.split('-');
                        zipCode = mainZip;
                        addonZipcode = addon ? `${mainZip}-${addon}` : mainZip;
                    }

                    const params = {
                        city,
                        state,
                        zipCode,
                        addonZipcode,
                        country: countryCode,
                        token,
                        orderId,
                        address1: '',
                    };

                    return updatePaypalShippingAddress(params)
                        .then(result => {
                            if (result.status !== 'SUCCESS') {
                                return actions.reject('STATE_ERROR');
                            }
                        })
                        .catch(() => {
                            return actions.reject('STATE_ERROR');
                        });
                },
                [orderId, updatePaypalShippingAddress],
            );

            if (x_paypalLabelMessage) {
                return !disableLabelMessage ? (
                    <div className="paypal-checkout">
                        <AlertBanner className="paypal-alert-banner">
                            <AlertBannerDescription>{x_paypalLabelMessage}</AlertBannerDescription>
                        </AlertBanner>
                    </div>
                ) : null;
            }
            if (
                !paypalSDKOptions?.clientId ||
                !payPalReturnURL ||
                (isPrivateStore && x_isPayPalEnabledForPstore === 'false') ||
                x_isPayPalEnabled === 'false' ||
                !cartUrls
            ) {
                return <div className="paypal-checkout"></div>;
            }

            return (
                <>
                    {header}
                    <div className="paypal-checkout">
                        <PayPalButtons
                            fundingSource={fundingSource}
                            style={{
                                label: 'checkout',
                                size: 'responsive',
                                shape: 'sharp',
                                color: device !== 'mobile' ? 'silver' : 'white',
                                tagline: 'false',
                                ...btnStyle,
                            }}
                            createOrder={() => {
                                return payPalExpressCheckout(paypalExpressURL, returnUrl);
                            }}
                            onApprove={(data, actions) => {
                                const { orderID: token, payerID: payId } = data;
                                setIsLoaded({ loading: true });
                                return actions.redirect(returnUrl + '&token=' + token + '&PayerID=' + payId);
                            }}
                            onClick={onButtonClick}
                            disabled={scrollToError ? false : disabled}
                            onShippingAddressChange={
                                String(x_payPalShippingAddressCallbackToggle).toLowerCase() === 'true'
                                    ? handlePaypalShippingAddressChange
                                    : null
                            }
                        />
                        {showTAndC && <TermsAndConditions />}
                    </div>
                </>
            );
        },
    ),
    {
        components: ['cartSettings'],
    },
);
